import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, Library as LibraryIcon } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { libraryDashboardApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { formatDate } from '@/utils/format';
import { BarRow, Money, Stat } from './shared';

/**
 * Where the library stands today.
 *
 * <p>Every figure is one the database can state outright — a count, a sum, a
 * status. Nothing is projected or averaged. The outstanding-fines tile is absent
 * rather than zero for somebody who cannot see fines: zero would read as "the
 * school is owed nothing", which is a different and false statement.
 */
export function LibraryDashboardPage() {
  const dashboard = useQuery({
    queryKey: queryKeys.library.dashboard(undefined),
    queryFn: () => libraryDashboardApi.library(),
  });

  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }
  if (dashboard.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = dashboard.data;
  const busiestDay = Math.max(1, ...data.recentActivity.map((day) => day.count));
  const mostBorrowed = Math.max(1, ...data.popularTitles.map((title) => title.borrowings));

  return (
    <div>
      <PageHeader
        title="Library"
        description="Stock, borrowing and what is late."
        icon={<LibraryIcon className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Titles"
          value={data.totalTitles}
          hint={`${data.totalCopies} physical cop${data.totalCopies === 1 ? 'y' : 'ies'}`}
        />
        <Stat
          label="On the shelves"
          value={data.availableCopies}
          tone="success"
          hint={`${data.issuedCopies} out with readers`}
        />
        <Stat
          label="Overdue"
          value={data.overdueLoans}
          tone={data.overdueLoans > 0 ? 'danger' : 'neutral'}
          hint={
            data.overdueLoans > 0 ? (
              <Link to={ROUTES.overdueLoans} className="text-accent underline-offset-2 hover:underline">
                Chase them
              </Link>
            ) : (
              'Nothing late'
            )
          }
        />
        <Stat
          label="Waiting to be collected"
          value={data.readyForCollection}
          tone={data.readyForCollection > 0 ? 'warning' : 'neutral'}
          hint={`${data.queuedReservations} still queued`}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Members" value={data.activeMembers} hint="Able to borrow" />
        <Stat
          label="Due this week"
          value={data.dueThisWeek}
          hint={`${data.dueToday} due today`}
        />
        {data.outstandingFines !== null && (
          <Stat
            label="Fines owed"
            value={<Money value={data.outstandingFines} />}
            tone={data.outstandingFines > 0 ? 'warning' : 'neutral'}
            hint={
              <Link to={ROUTES.libraryFines} className="text-accent underline-offset-2 hover:underline">
                See the fines
              </Link>
            }
          />
        )}
        <Stat
          label="Out of circulation"
          value={data.missingCopies + data.lostCopies + data.damagedCopies}
          tone={data.missingCopies + data.lostCopies + data.damagedCopies > 0 ? 'warning' : 'neutral'}
          hint={`${data.missingCopies} missing · ${data.lostCopies} lost · ${data.damagedCopies} damaged`}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Borrowing, last thirty days
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Books issued each day. Days with no borrowing are simply absent.
          </p>
          {data.recentActivity.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">Nothing has been borrowed yet.</p>
          ) : (
            <div>
              {data.recentActivity.map((day) => (
                <BarRow
                  key={day.date}
                  label={formatDate(day.date)}
                  value={day.count}
                  max={busiestDay}
                />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Most borrowed
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Over the last ninety days, counted in the database rather than in the browser.
          </p>
          {data.popularTitles.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">
              Not enough borrowing yet to rank anything.
            </p>
          ) : (
            <div>
              {data.popularTitles.map((title) => (
                <BarRow
                  key={title.bookId}
                  label={title.title}
                  value={title.borrowings}
                  max={mostBorrowed}
                  suffix={`${title.borrowings}×`}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-4">
        <Link
          to={ROUTES.circulationDesk}
          className="inline-flex items-center gap-2 border border-line bg-bg px-3 py-2 text-sm font-semibold text-ink hover:border-accent"
        >
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          Open the issue desk
        </Link>
      </div>
    </div>
  );
}
